export const getCroppedImg = async (
	imageSrc: string,
	pixelCrop: { x: number; y: number; width: number; height: number },
	canvasSize?: { width: number; height: number },
): Promise<string> => {
	const createImage = (src: string): Promise<HTMLImageElement> => {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.crossOrigin = "anonymous";
			img.src = src;
			img.onload = () => resolve(img);
			img.onerror = (err) => reject(err);
		});
	};

	const image = await createImage(imageSrc);

	// Check if canvas size differs from original image size (manual placement/resizing)
	if (canvasSize && (canvasSize.width !== image.width || canvasSize.height !== image.height)) {
		return await createCanvasRepresentation(image, pixelCrop, canvasSize);
	}

	// Regular crop mode: extract only the cropped area
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");

	if (!ctx) {
		throw new Error("Failed to create canvas context");
	}

	canvas.width = pixelCrop.width;
	canvas.height = pixelCrop.height;

	ctx.fillStyle = "rgba(0, 0, 0, 0)";
	ctx.fillRect(0, 0, canvas.width, canvas.height);

	ctx.drawImage(
		image,
		pixelCrop.x,
		pixelCrop.y,
		pixelCrop.width,
		pixelCrop.height,
		0,
		0,
		pixelCrop.width,
		pixelCrop.height,
	);

	return new Promise((resolve, reject) => {
		try {
			const base64Image = canvas.toDataURL("image/png");
			resolve(base64Image);
		} catch (error) {
			reject(new Error("Failed to generate Base64 image"));
		}
	});
};

// Create a canvas representation when canvas size differs from original
const createCanvasRepresentation = async (
	image: HTMLImageElement,
	pixelCrop: { x: number; y: number; width: number; height: number },
	canvasSize: { width: number; height: number },
): Promise<string> => {
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");

	if (!ctx) {
		throw new Error("Failed to create canvas context");
	}

	// Set canvas to target dimensions (e.g., 538x430 for 5:4 aspect ratio)
	canvas.width = canvasSize.width;
	canvas.height = canvasSize.height;

	// Fill with transparent background
	ctx.fillStyle = "rgba(0, 0, 0, 0)";
	ctx.fillRect(0, 0, canvas.width, canvas.height);

	// The pixelCrop represents the area that is visible in the cropper UI
	// We need to extract that area and place it to fill the entire target canvas
	
	// Extract the cropped area from the original image
	const sourceX = pixelCrop.x;
	const sourceY = pixelCrop.y;
	const sourceWidth = pixelCrop.width;
	const sourceHeight = pixelCrop.height;

	// Draw the cropped area to fill the entire target canvas
	// This scales the cropped area to match the target canvas dimensions
	ctx.drawImage(
		image,
		sourceX,
		sourceY,
		sourceWidth,
		sourceHeight,
		0,
		0,
		canvas.width,
		canvas.height,
	);

	return new Promise((resolve, reject) => {
		try {
			const base64Image = canvas.toDataURL("image/png");
			resolve(base64Image);
		} catch (error) {
			reject(new Error("Failed to generate canvas representation"));
		}
	});
};